import React from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { formatDate } from '../../../utils/formatters';

const TemplateLibrary = ({ templates, onEdit, onDelete }) => {
  const getCategoryIcon = (type) => {
    const icons = {
      assignment: 'UserPlus',
      decision: 'CheckCircle',
      deadline_reminder: 'Clock',
      status_update: 'Info',
      custom: 'Mail'
    };
    return icons?.[type] || 'Mail';
  };

  const getCategoryColor = (type) => {
    const colors = {
      assignment: 'bg-primary-subtle text-primary',
      decision: 'bg-success-subtle text-success',
      deadline_reminder: 'bg-accent-subtle text-accent',
      status_update: 'bg-muted text-foreground',
      custom: 'bg-muted text-foreground'
    };
    return colors?.[type] || 'bg-muted text-foreground';
  };

  const getCategoryLabel = (type) => {
    const labels = {
      assignment: 'Reviewer Assignment',
      decision: 'Paper Decision',
      deadline_reminder: 'Deadline Reminder',
      status_update: 'Status Update',
      custom: 'General Communication'
    };
    return labels?.[type] || 'Custom';
  };

  const groupedTemplates = templates?.reduce((acc, template) => {
    const category = template?.notificationType || 'custom';
    if (!acc?.[category]) {
      acc[category] = [];
    }
    acc?.[category]?.push(template);
    return acc;
  }, {});

  if (templates?.length === 0) {
    return (
      <div className="bg-card rounded-lg border border-border p-12 text-center">
        <Icon name="FileText" size={64} className="text-muted-foreground mx-auto mb-4" />
        <h3 className="text-xl font-semibold text-foreground mb-2">No Templates Found</h3>
        <p className="text-muted-foreground mb-6">
          Create your first email template to get started with automated communications.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {Object?.entries(groupedTemplates)?.map(([category, categoryTemplates]) => (
        <div key={category}>
          <div className="flex items-center space-x-3 mb-4">
            <div className={`p-2 rounded-lg ${getCategoryColor(category)}`}>
              <Icon name={getCategoryIcon(category)} size={20} />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-foreground">{getCategoryLabel(category)}</h2>
              <p className="text-sm text-muted-foreground">{categoryTemplates?.length} template(s)</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {categoryTemplates?.map(template => (
              <div
                key={template?.id}
                className="bg-card rounded-lg border border-border p-5 hover:shadow-lg transition-all duration-200 hover:border-primary/50"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-foreground mb-1">{template?.name}</h3>
                    {template?.isDefault && (
                      <span className="inline-flex items-center px-2 py-0.5 bg-primary/10 text-primary text-xs font-medium">
                        <Icon name="Star" size={12} className="mr-1" />
                        Default
                      </span>
                    )}
                  </div>
                </div>

                <div className="mb-4">
                  <p className="text-sm font-medium text-foreground mb-1">Subject:</p>
                  <p className="text-sm text-muted-foreground line-clamp-2">{template?.subject}</p>
                </div>

                <div className="mb-4">
                  <p className="text-sm text-muted-foreground line-clamp-3">{template?.message}</p>
                </div>

                <div className="flex items-center justify-between pt-4 border-t border-border">
                  <span className="text-xs text-muted-foreground">
                    {formatDate(template?.updatedAt || template?.createdAt)}
                  </span>
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onEdit?.(template)}
                    >
                      <Icon name="Edit" size={14} className="mr-1" />
                      Edit
                    </Button>
                    {!template?.isDefault && (
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => onDelete?.(template?.id)}
                      >
                        <Icon name="Trash2" size={14} />
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default TemplateLibrary;